import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import profileApi from '../api/profileApi';
import { Profile, LoadingStatus } from '../../types';

interface ProfileState {
  myProfile: Profile | null;
  viewedProfile: Profile | null;
  setupStep: number;
  status: LoadingStatus;
  error: string | null;
}

const initialState: ProfileState = {
  myProfile: null,
  viewedProfile: null,
  setupStep: 1,
  status: 'idle',
  error: null,
};

export const fetchMyProfile = createAsyncThunk('profile/fetchMine', async (_, { rejectWithValue }) => {
  try {
    const res = await profileApi.getMyProfile();
    return res.data as Profile;
  } catch (err: unknown) {
    const e = err as { response?: { status?: number; data?: { message?: string } } };
    // 404 means no profile exists yet — treat as valid "no profile" state
    if (e.response?.status === 404) return null;
    return rejectWithValue(e.response?.data?.message || 'Failed to fetch profile');
  }
});

export const createProfile = createAsyncThunk(
  'profile/create',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const res = await profileApi.createProfile(formData);
      return res.data as Profile;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to create profile');
    }
  },
);

export const updateProfile = createAsyncThunk(
  'profile/update',
  async (data: FormData | Record<string, unknown>, { rejectWithValue }) => {
    try {
      const res = await profileApi.updateMyProfile(data);
      return res.data as Profile;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to update profile');
    }
  },
);

export const addGalleryImage = createAsyncThunk(
  'profile/addGalleryImage',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const res = await profileApi.addGalleryImage(formData);
      return res.data as Profile;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to add image');
    }
  },
);

export const fetchProfileById = createAsyncThunk(
  'profile/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await profileApi.getProfileById(id);
      return res.data as Profile;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch profile');
    }
  },
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setSetupStep(state, action) { state.setupStep = action.payload; },
    clearProfileError(state) { state.error = null; },
    markSetupComplete(state) {
      if (state.myProfile) {
        state.myProfile.isSetupComplete = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyProfile.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchMyProfile.fulfilled, (state, { payload }) => {
        state.myProfile = payload ?? null;
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(fetchMyProfile.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(createProfile.fulfilled, (state, { payload }) => {
        state.myProfile = payload;
        state.status = 'succeeded';
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.myProfile = payload;
      })
      .addCase(addGalleryImage.fulfilled, (state, { payload }) => {
        state.myProfile = payload;
      })
      .addCase(fetchProfileById.fulfilled, (state, { payload }) => {
        state.viewedProfile = payload;
      });
  },
});

export const { setSetupStep, clearProfileError, markSetupComplete } = profileSlice.actions;
export default profileSlice.reducer;

export const selectMyProfile = (state: { profile: ProfileState }) => state.profile.myProfile;
export const selectSetupStep = (state: { profile: ProfileState }) => state.profile.setupStep;
export const selectIsProfileComplete = (state: { profile: ProfileState }) =>
  !!state.profile.myProfile?.profilePhoto && !!state.profile.myProfile?.dateOfBirth;
export const selectViewedProfile = (state: { profile: ProfileState }) => state.profile.viewedProfile;
export const selectProfileStatus = (state: { profile: ProfileState }) => state.profile.status;
